import { useCallback, useEffect, useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { askUserApi, meApi, spaceInboxApi } from '../services/api';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { PlatformEvent } from '../types/events';
import type {
  AskUserAnswer,
  AskUserRequest,
  InboxCountsResponse,
  InboxItem,
  InboxItemStatus,
  InboxItemWithSource,
  InboxRefType,
} from '../types/inbox';
import type { PaginatedResponse } from '../types';

const PAGE_SIZE = 30;

type InboxFilter = {
  status?: InboxItemStatus | 'all';
  refType?: InboxRefType;
};

type InboxPage = PaginatedResponse<InboxItem>;
type InboxQueryData = InfiniteData<InboxPage>;

const inboxListKey = (f: InboxFilter) =>
  ['inbox', 'list', { status: f.status ?? 'pending', refType: f.refType ?? null }] as const;

type InboxExpand = 'source' | 'labels';

const inboxItemKey = (id: string, expand?: InboxExpand | InboxExpand[]) =>
  ['inbox', 'item', id, normalizeExpand(expand)] as const;

function normalizeExpand(
  expand?: InboxExpand | InboxExpand[],
): string | null {
  if (!expand) return null;
  return Array.isArray(expand) ? [...expand].sort().join(',') : expand;
}

function replaceInPages(
  data: InboxQueryData | undefined,
  id: string,
  updater: (item: InboxItem) => InboxItem,
): InboxQueryData | undefined {
  if (!data) return data;
  let changed = false;
  const pages = data.pages.map((page) => {
    const idx = page.items.findIndex((x) => x.inboxItemId === id);
    if (idx === -1) return page;
    changed = true;
    const items = page.items.slice();
    items[idx] = updater(items[idx]);
    return { ...page, items };
  });
  return changed ? { ...data, pages } : data;
}

function matchesFilter(item: InboxItem, filter: InboxFilter): boolean {
  const status = filter.status ?? 'pending';
  if (status !== 'all' && item.status !== status) return false;
  if (filter.refType && item.refType !== filter.refType) return false;
  return true;
}

function removeFromPages(data: InboxQueryData | undefined, id: string): InboxQueryData | undefined {
  if (!data) return data;
  let changed = false;
  const pages = data.pages.map((page) => {
    const idx = page.items.findIndex((x) => x.inboxItemId === id);
    if (idx === -1) return page;
    changed = true;
    return { ...page, items: page.items.filter((x) => x.inboxItemId !== id) };
  });
  return changed ? { ...data, pages } : data;
}

function prependItem(data: InboxQueryData | undefined, item: InboxItem): InboxQueryData | undefined {
  if (!data || data.pages.length === 0) return data;
  // Avoid duplicate inserts when the row arrived via both REST and WS.
  for (const page of data.pages) {
    if (page.items.some((x) => x.inboxItemId === item.inboxItemId)) return data;
  }
  const [first, ...rest] = data.pages;
  const next: InboxPage = { ...first, items: [item, ...first.items] };
  return { ...data, pages: [next, ...rest] };
}

export function useInbox(filter: InboxFilter = {}) {
  const queryClient = useQueryClient();
  const { subscribe, isConnected, diagnostics } = usePlatformEvents();

  const queryKey = useMemo(() => inboxListKey(filter), [filter.status, filter.refType]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery<InboxPage, Error, InboxQueryData, typeof queryKey, string | undefined>({
    queryKey,
    queryFn: ({ pageParam }) =>
      meApi.inbox({
        status: filter.status ?? 'pending',
        refType: filter.refType,
        limit: PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextToken,
    staleTime: 0,
    retry: false,
  });

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  const handleEvent = useCallback(
    (event: PlatformEvent) => {
      const type = event.eventType;
      const d = event.data as Record<string, unknown>;

      // Backend emits `inbox.item_added`; `inbox.item.added` is a transitional
      // alias kept here so a transient dot-form emission doesn't drop the row.
      if (type === 'inbox.item_added' || type === 'inbox.item.added') {
        const item = d.item as InboxItem | undefined;
        if (!item || !matchesFilter(item, filter)) return;
        queryClient.setQueryData<InboxQueryData>(queryKey, (prev) => prependItem(prev, item));
        return;
      }

      // Relabel / generic update — patch the row in place so chips refresh
      // without dropping or reordering. Event carries the full new item.
      if (type === 'inbox.item_updated' || type === 'inbox.item.updated') {
        const item = d.item as InboxItem | undefined;
        const id = item?.inboxItemId ?? (d.inboxItemId as string | undefined);
        if (!id) return;
        queryClient.setQueryData<InboxQueryData>(queryKey, (prev) =>
          replaceInPages(prev, id, (existing) => (item ? { ...existing, ...item } : existing)),
        );
        queryClient.invalidateQueries({ queryKey: ['inbox', 'item', id] });
        return;
      }

      // Canonical: underscore form. Dot variants are kept as a transitional
      // alias so we don't regress against any straggler producers.
      if (
        type === 'inbox.item_resolved' ||
        type === 'inbox.item_cancelled' ||
        type === 'inbox.item_expired' ||
        type === 'inbox.item.resolved' ||
        type === 'inbox.item.cancelled' ||
        type === 'inbox.item.expired'
      ) {
        const id = (d.inboxItemId as string) ?? '';
        if (!id) return;
        // When the active filter is "pending", a status change should drop
        // the row. When "all", refresh the row so its status reflects.
        if ((filter.status ?? 'pending') !== 'all') {
          queryClient.setQueryData<InboxQueryData>(queryKey, (prev) => removeFromPages(prev, id));
        } else {
          queryClient.invalidateQueries({ queryKey });
        }
        // Also invalidate the detail query so an open expanded card converges
        // to the new status banner (answered/cancelled/expired).
        queryClient.invalidateQueries({ queryKey: ['inbox', 'item', id] });
        return;
      }

      // Run-side companions: list cache is already handled by inbox.item_*,
      // but we still invalidate the detail so an open card re-renders.
      if (
        type === 'agent.run.ask_user_answered' ||
        type === 'agent.run.ask_user_cancelled'
      ) {
        const inboxItemId = d.inboxItemId as string | undefined;
        if (inboxItemId) {
          queryClient.invalidateQueries({ queryKey: ['inbox', 'item', inboxItemId] });
        }
        return;
      }

      // agent.run.ask_user_requested is defensive only — inbox.item_added is
      // the canonical creation event. No-op so the wildcard subscription
      // doesn't accidentally fall through to a list refetch.
      if (type === 'agent.run.ask_user_requested') {
        return;
      }
    },
    [filter, queryClient, queryKey],
  );

  useEffect(() => {
    // Wildcards instead of explicit names — matches the useOverview.ts
    // pattern and future-proofs against new event flavors.
    const unsub = subscribe(
      [
        { eventType: 'inbox.*' },
        { eventType: 'agent.run.ask_user_*' },
      ],
      handleEvent,
    );
    return unsub;
  }, [subscribe, handleEvent]);

  return {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    refetch,
    error,
    isConnected,
    diagnostics,
  };
}

export function useInboxItem(
  inboxItemId: string | null | undefined,
  opts: { expand?: InboxExpand | InboxExpand[]; enabled?: boolean } = {},
) {
  const enabled = (opts.enabled ?? true) && !!inboxItemId;
  return useQuery<InboxItemWithSource>({
    queryKey: inboxItemKey(inboxItemId ?? '', opts.expand),
    queryFn: () => meApi.inboxItem(inboxItemId as string, { expand: opts.expand }),
    enabled,
    staleTime: 0,
    retry: false,
  });
}

interface AnswerArgs {
  spaceId: string;
  requestId: string;
  inboxItemId: string;
  answers: AskUserAnswer[];
}

export function useAnswerAskUser(filter: InboxFilter = {}) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => inboxListKey(filter), [filter.status, filter.refType]);

  return useMutation<AskUserRequest, Error, AnswerArgs>({
    mutationFn: ({ spaceId, requestId, answers }) =>
      askUserApi.answer(spaceId, requestId, answers),
    onSuccess: (_request, { inboxItemId }) => {
      if ((filter.status ?? 'pending') !== 'all') {
        queryClient.setQueryData<InboxQueryData>(queryKey, (prev) =>
          removeFromPages(prev, inboxItemId),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['inbox', 'item', inboxItemId] });
    },
  });
}

interface CancelArgs {
  spaceId: string;
  requestId: string;
  inboxItemId: string;
  reason?: string;
}

interface RelabelArgs {
  spaceId: string;
  inboxItemId: string;
  add?: string[];
  remove?: string[];
}

/**
 * Atomic add/remove on an inbox item's labels. Optimistically patches the
 * cross-space `useInbox` cache(s); the canonical `inbox.item_updated`
 * event reconciles afterwards and also drives the per-space `useSpaceInbox`
 * caches.
 */
export function useRelabelInboxItem() {
  const queryClient = useQueryClient();
  return useMutation<InboxItem, Error, RelabelArgs>({
    mutationFn: ({ spaceId, inboxItemId, add, remove }) =>
      spaceInboxApi.relabel(spaceId, inboxItemId, { add, remove }),
    onMutate: async ({ spaceId, inboxItemId, add, remove }) => {
      // Optimistically patch every loaded inbox-list cache that holds the
      // item. React Query's setQueriesData iterates by key prefix.
      queryClient.setQueriesData<InboxQueryData>(
        { queryKey: ['inbox', 'list'] },
        (prev) =>
          replaceInPages(prev, inboxItemId, (it) => {
            const nextIds = new Set(it.labelIds ?? []);
            for (const r of remove ?? []) nextIds.delete(r);
            for (const a of add ?? []) nextIds.add(a);
            return { ...it, labelIds: Array.from(nextIds) };
          }),
      );
      // Mirror the patch onto the per-label pending counts so the rail
      // moves immediately. The follow-up `inbox.item_updated` event's
      // diff reconciles to zero (cache item already carries the new ids
      // by then), so no double-count.
      queryClient.setQueryData<InboxCountsResponse>(
        ['me', 'inboxCounts'],
        (prev) => {
          if (!prev) return prev;
          const space = prev.spaces[spaceId] ?? { all: 0, byLabel: {} };
          const byLabel = { ...space.byLabel };
          for (const id of remove ?? []) {
            byLabel[id] = Math.max(0, (byLabel[id] ?? 0) - 1);
          }
          for (const id of add ?? []) {
            byLabel[id] = (byLabel[id] ?? 0) + 1;
          }
          return {
            ...prev,
            spaces: { ...prev.spaces, [spaceId]: { ...space, byLabel } },
          };
        },
      );
    },
    onSuccess: (_item, { inboxItemId, spaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['inbox', 'item', inboxItemId] });
      // Space-scoped lists drive the team queue; just invalidate so the
      // backend's authoritative labels payload (incl. `labels[]`) refills.
      queryClient.invalidateQueries({ queryKey: ['spaceInbox', 'list'] });
      // Bump any open `useLabels(spaceId)` consumer in case counts changed.
      void spaceId;
    },
  });
}

export function useCancelAskUser(filter: InboxFilter = {}) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => inboxListKey(filter), [filter.status, filter.refType]);

  return useMutation<AskUserRequest, Error, CancelArgs>({
    mutationFn: ({ spaceId, requestId, reason }) =>
      askUserApi.cancel(spaceId, requestId, reason),
    onSuccess: (_request, { inboxItemId }) => {
      if ((filter.status ?? 'pending') !== 'all') {
        queryClient.setQueryData<InboxQueryData>(queryKey, (prev) =>
          removeFromPages(prev, inboxItemId),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['inbox', 'item', inboxItemId] });
    },
  });
}
